import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { X, CheckCircle, ArrowRight, ArrowLeft, Sparkles, Share2, Copy, Check } from 'lucide-react';
import { PART1_QUESTIONS, SYMPTOMS, PILLAR_DETAILS, CATEGORY_TO_SUBCATEGORY, getCategoryClassroomUrl } from './wellnessAssessmentData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProgressBar = ({ step }) => {
  const steps = ['intro', 'part1', 'part2', 'results'];
  const labels = ['Welcome', 'Focus Area', 'Symptoms', 'Results'];
  const currentIndex = steps.indexOf(step);
  return (
    <div className="flex items-center gap-1.5 px-6 py-3 bg-cream/50 border-b border-burgundy/10">
      {labels.map((label, i) => (
        <div key={label} className="flex items-center flex-1">
          <div className="flex items-center gap-1.5 flex-1">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 transition-all duration-300 ${
              i < currentIndex ? 'bg-dark-teal text-cream' : i === currentIndex ? 'bg-burgundy text-cream ring-2 ring-burgundy/20 ring-offset-1' : 'bg-burgundy/10 text-burgundy/40'
            }`}>{i < currentIndex ? <CheckCircle className="w-3.5 h-3.5" /> : i + 1}</div>
            <span className={`text-[10px] font-medium hidden sm:block ${i <= currentIndex ? 'text-burgundy' : 'text-burgundy/40'}`}>{label}</span>
          </div>
          {i < 3 && <div className={`h-0.5 flex-1 mx-1.5 rounded transition-all duration-300 ${i < currentIndex ? 'bg-dark-teal' : 'bg-burgundy/10'}`} />}
        </div>
      ))}
    </div>
  );
};

const WellnessAssessment = ({ isOpen, onClose, user, onUserUpdate }) => {
  const [step, setStep] = useState('intro');
  const [part1Answers, setPart1Answers] = useState({});
  const [primaryPillar, setPrimaryPillar] = useState(null);
  const [part2Symptoms, setPart2Symptoms] = useState({});
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [existingAssessment, setExistingAssessment] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [expandedPillar, setExpandedPillar] = useState(null);

  const fetchExistingAssessment = async (userId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/users/${userId}/wellness-assessment`);
      if (response.data.assessment) setExistingAssessment(response.data.assessment);
    } catch (error) { console.error('Error fetching assessment:', error); }
  };

  useEffect(() => {
    if (isOpen && user?.id) fetchExistingAssessment(user.id);
    // eslint-disable-next-line
  }, [isOpen, user?.id]);

  const handlePart1Answer = (qid, answer) => setPart1Answers(prev => ({ ...prev, [qid]: answer }));

  const calculatePrimaryPillar = () => {
    const counts = { A: 0, B: 0, C: 0 };
    Object.values(part1Answers).forEach(a => { counts[a]++; });
    if (counts.A >= counts.B && counts.A >= counts.C) return 'BODY';
    if (counts.B > counts.A && counts.B >= counts.C) return 'MIND';
    return 'VITALITY';
  };

  const handlePart1Complete = () => {
    if (Object.keys(part1Answers).length < 5) return;
    const pillar = calculatePrimaryPillar();
    setPrimaryPillar(pillar);
    setExpandedPillar(pillar);
    setStep('part2');
  };

  const handleSymptomToggle = (category, symptom) => {
    setPart2Symptoms(prev => {
      const arr = prev[category] || [];
      return { ...prev, [category]: arr.includes(symptom) ? arr.filter(s => s !== symptom) : [...arr, symptom] };
    });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const categoryCounts = {};
      Object.entries(part2Symptoms).forEach(([cat, syms]) => { if (syms.length > 0) categoryCounts[cat] = syms.length; });
      const sortedCats = Object.entries(categoryCounts).sort((a, b) => b[1] - a[1]).map(([cat]) => cat);
      const totalSymptoms = Object.values(part2Symptoms).reduce((s, a) => s + a.length, 0);

      const pillarSymptomCounts = { BODY: 0, MIND: 0, VITALITY: 0 };
      Object.entries(part2Symptoms).forEach(([cat, syms]) => {
        const m = CATEGORY_TO_SUBCATEGORY[cat];
        if (m) {
          if (m.pillar === 'radiant_body') pillarSymptomCounts.BODY += syms.length;
          else if (m.pillar === 'balanced_mind') pillarSymptomCounts.MIND += syms.length;
          else if (m.pillar === 'enduring_vitality') pillarSymptomCounts.VITALITY += syms.length;
        }
      });

      const calculatedResults = {
        primary_pillar: primaryPillar,
        top_subcategories: sortedCats.slice(0, 4),
        top_categories: sortedCats.slice(0, 4),
        total_symptoms: totalSymptoms,
        pillar_symptom_counts: pillarSymptomCounts
      };

      if (user?.id) {
        await axios.post(`${BACKEND_URL}/api/users/${user.id}/wellness-assessment`, {
          part1_answers: part1Answers,
          part2_symptoms: part2Symptoms,
          primary_pillar: primaryPillar,
          top_subcategories: sortedCats.slice(0, 4)
        });
        // Refresh user data so checklist updates
        if (onUserUpdate) {
          try {
            const userRes = await axios.get(`${BACKEND_URL}/api/users/${user.id}`);
            onUserUpdate(userRes.data);
          } catch (e) { console.log('Could not refresh user data'); }
        }
      }

      setResults(calculatedResults);
      setStep('results');
    } catch (error) { console.error('Error saving assessment:', error); }
    finally { setLoading(false); }
  };

  const handleRetake = () => { setPart1Answers({}); setPart2Symptoms({}); setPrimaryPillar(null); setResults(null); setExpandedPillar(null); setStep('intro'); };
  const handleClose = () => { handleRetake(); onClose(); };
  const handleCopyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/wellness-assessment`).then(() => {
      setLinkCopied(true); setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  if (!isOpen) return null;
  const isPart1Complete = Object.keys(part1Answers).length === 5;
  const hasPart2Symptoms = Object.values(part2Symptoms).some(s => s.length > 0);
  const pillarOrder = primaryPillar ? [primaryPillar, ...['BODY', 'MIND', 'VITALITY'].filter(p => p !== primaryPillar)] : ['BODY', 'MIND', 'VITALITY'];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4" data-testid="wellness-assessment-modal">
      <Card className="w-full max-w-4xl max-h-[90vh] flex flex-col border-0 shadow-2xl bg-cream">
        <CardHeader className="bg-gradient-to-r from-burgundy to-dark-teal text-cream shrink-0 pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl md:text-3xl font-serif" data-testid="assessment-modal-title">Wellness Assessment</CardTitle>
              <p className="text-cream/70 text-sm mt-1">
                {step === 'intro' && 'Discover your primary wellness focus'}
                {step === 'part1' && 'Part 1: Identify Your Focus Area'}
                {step === 'part2' && 'Part 2: Select Your Symptoms'}
                {step === 'results' && 'Your Personalized Wellness Profile'}
              </p>
            </div>
            <Button variant="ghost" onClick={handleClose} className="text-cream hover:bg-cream/20" data-testid="assessment-modal-close"><X className="h-5 w-5" /></Button>
          </div>
        </CardHeader>
        <ProgressBar step={step} />
        <CardContent className="p-5 md:p-6 overflow-y-auto flex-1 max-h-[calc(90vh-10rem)]">

          {/* INTRO */}
          {step === 'intro' && (
            <div className="text-center py-4" data-testid="assessment-modal-intro">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-burgundy/10 text-burgundy rounded-full text-sm font-medium mb-5">
                <Sparkles className="w-3.5 h-3.5" /> 2-Minute Wellness Check
              </div>
              <h2 className="text-2xl sm:text-3xl font-serif font-bold text-burgundy leading-tight mb-3">Where Does Your Wellness Need Attention?</h2>
              <p className="text-burgundy/60 max-w-lg mx-auto mb-8 text-sm">Identifies your primary focus and connects you to the most relevant content.</p>
              <div className="grid grid-cols-3 gap-3 mb-8 max-w-lg mx-auto">
                {[{ p: 'Radiant Body', d: 'Physical symptoms & body changes' }, { p: 'Balanced Mind', d: 'Clarity, mood & emotional balance' }, { p: 'Enduring Vitality', d: 'Energy, sleep & restoration' }].map((item, i) => (
                  <div key={item.p} className="bg-white rounded-xl p-3 border border-burgundy/10 text-center">
                    <div className="w-8 h-8 rounded-full bg-burgundy/10 flex items-center justify-center text-burgundy font-bold text-xs mx-auto mb-2">{i + 1}</div>
                    <h3 className="font-serif font-bold text-burgundy text-sm mb-0.5">{item.p}</h3>
                    <p className="text-[11px] text-burgundy/50 leading-tight">{item.d}</p>
                  </div>
                ))}
              </div>
              {existingAssessment && (
                <div className="mb-6 p-3 bg-gold/10 border border-gold/30 rounded-lg text-left max-w-md mx-auto">
                  <h4 className="font-semibold text-burgundy text-sm mb-1">Previous Results:</h4>
                  <p className="text-burgundy/70 text-sm"><strong>Focus:</strong> {existingAssessment.primary_pillar} &middot; <strong>Areas:</strong> {existingAssessment.top_subcategories?.join(', ') || 'N/A'}</p>
                </div>
              )}
              <Button onClick={() => setStep('part1')} className="bg-gradient-to-r from-burgundy to-dark-teal text-cream hover:opacity-90 px-8 py-5 rounded-full shadow-lg text-base" data-testid="assessment-modal-start-btn">
                Start Assessment <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <div className="mt-6 pt-4 border-t border-burgundy/10">
                <p className="text-xs text-burgundy/50 mb-2">Share this assessment:</p>
                <button onClick={handleCopyLink} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-burgundy/15 rounded-full text-sm text-burgundy/70 hover:bg-burgundy/5 transition-colors" data-testid="assessment-modal-copy-link">
                  {linkCopied ? <Check className="w-3.5 h-3.5 text-dark-teal" /> : <Copy className="w-3.5 h-3.5" />}
                  {linkCopied ? 'Link copied!' : 'Copy shareable link'}
                </button>
              </div>
            </div>
          )}

          {/* PART 1 */}
          {step === 'part1' && (
            <div className="space-y-4" data-testid="assessment-modal-part1">
              {PART1_QUESTIONS.map((q, idx) => (
                <div key={q.id} className="bg-white rounded-xl p-4 border border-burgundy/10" data-testid={`modal-q${idx + 1}`}>
                  <h3 className="text-sm md:text-base font-semibold text-burgundy mb-3"><span className="text-dark-teal mr-1.5">{idx + 1}.</span>{q.question}</h3>
                  <div className="space-y-2">
                    {Object.entries(q.options).map(([letter, text]) => (
                      <label key={letter} className={`flex items-start gap-2.5 p-3 rounded-lg border-2 cursor-pointer transition-all ${part1Answers[q.id] === letter ? 'border-burgundy bg-burgundy/5' : 'border-burgundy/15 hover:border-burgundy/30'}`} data-testid={`modal-q${idx + 1}-option-${letter}`}>
                        <div className={`mt-0.5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${part1Answers[q.id] === letter ? 'border-burgundy bg-burgundy' : 'border-burgundy/30'}`} style={{ width: '18px', height: '18px' }}>
                          {part1Answers[q.id] === letter && <div className="w-2 h-2 rounded-full bg-cream" />}
                        </div>
                        <input type="radio" name={q.id} value={letter} checked={part1Answers[q.id] === letter} onChange={() => handlePart1Answer(q.id, letter)} className="sr-only" />
                        <span className="text-burgundy/80 text-sm">{text}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              <div className="flex justify-between items-center pt-4">
                <Button onClick={() => setStep('intro')} variant="outline" className="border-burgundy/20 text-burgundy hover:bg-burgundy/5"><ArrowLeft className="mr-1.5 h-4 w-4" /> Back</Button>
                <Button onClick={handlePart1Complete} disabled={!isPart1Complete} className="bg-gradient-to-r from-burgundy to-dark-teal text-cream hover:opacity-90 px-6 py-4 rounded-full disabled:opacity-40" data-testid="modal-part1-continue">
                  Continue <ArrowRight className="ml-1.5 h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* PART 2: ALL pillars */}
          {step === 'part2' && (
            <div className="space-y-3" data-testid="assessment-modal-part2">
              <p className="text-sm text-burgundy/60 mb-2">Check everything that applies. Your primary focus is expanded, but check other areas too.</p>
              {pillarOrder.map((pillarKey) => {
                const pillar = SYMPTOMS[pillarKey];
                const isPrimary = pillarKey === primaryPillar;
                const isExpanded = expandedPillar === pillarKey;
                const count = Object.keys(pillar.categories).reduce((s, c) => s + (part2Symptoms[c]?.length || 0), 0);
                return (
                  <div key={pillarKey} className={`rounded-lg border-2 overflow-hidden ${isPrimary ? 'border-burgundy/30' : 'border-burgundy/10'}`}>
                    <button onClick={() => setExpandedPillar(isExpanded ? null : pillarKey)} className={`w-full flex items-center justify-between p-3 ${isPrimary ? 'bg-burgundy/5' : 'bg-white'}`}>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${pillarKey === 'BODY' ? 'bg-burgundy text-cream' : pillarKey === 'MIND' ? 'bg-dark-teal text-cream' : 'bg-gold text-burgundy'}`}>{pillar.label}</span>
                        {isPrimary && <span className="text-[10px] text-burgundy bg-gold/30 px-1.5 py-0.5 rounded-full">Primary</span>}
                        {count > 0 && <span className="text-[10px] text-burgundy/50">{count} selected</span>}
                      </div>
                      <svg className={`w-4 h-4 text-burgundy/40 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                    </button>
                    {isExpanded && (
                      <div className="p-3 pt-0 space-y-3">
                        {Object.entries(pillar.categories).map(([category, symptoms]) => (
                          <div key={category} className="bg-white rounded-lg p-3 border border-burgundy/10 mt-2">
                            <h4 className="font-serif font-bold text-burgundy text-xs mb-2">{category}</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                              {symptoms.map((symptom, si) => {
                                const isChecked = part2Symptoms[category]?.includes(symptom) || false;
                                return (
                                  <label key={si} className={`flex items-start gap-2 p-2 rounded border cursor-pointer transition-all ${isChecked ? 'border-dark-teal bg-dark-teal/5' : 'border-burgundy/10 hover:border-burgundy/20'}`}>
                                    <div className={`mt-0.5 w-3.5 h-3.5 rounded flex items-center justify-center shrink-0 border-2 ${isChecked ? 'border-dark-teal bg-dark-teal' : 'border-burgundy/25'}`}>
                                      {isChecked && <CheckCircle className="w-2.5 h-2.5 text-cream" />}
                                    </div>
                                    <input type="checkbox" checked={isChecked} onChange={() => handleSymptomToggle(category, symptom)} className="sr-only" />
                                    <span className="text-[11px] text-burgundy/75">{symptom}</span>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              <div className="flex justify-between items-center pt-3">
                <Button onClick={() => setStep('part1')} variant="outline" className="border-burgundy/20 text-burgundy hover:bg-burgundy/5"><ArrowLeft className="mr-1.5 h-4 w-4" /> Back</Button>
                <Button onClick={handleSubmit} disabled={loading || !hasPart2Symptoms} className="bg-gradient-to-r from-burgundy to-dark-teal text-cream hover:opacity-90 px-6 py-4 rounded-full disabled:opacity-40" data-testid="modal-see-results">
                  {loading ? 'Saving...' : 'See My Results'} <CheckCircle className="ml-1.5 h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* RESULTS */}
          {step === 'results' && results && (
            <div className="space-y-4" data-testid="assessment-modal-results">
              <div className={`bg-gradient-to-br ${PILLAR_DETAILS[results.primary_pillar].color} rounded-xl p-5 text-cream`} data-testid="modal-result-pillar">
                <p className="text-cream/70 text-xs uppercase tracking-wider mb-0.5">Primary Focus</p>
                <h3 className="text-2xl font-serif font-bold text-white" style={{ textShadow: '0 0 20px rgba(212,175,55,0.6), 0 2px 4px rgba(0,0,0,0.3)' }}>{PILLAR_DETAILS[results.primary_pillar].fullLabel}</h3>
                <p className="text-cream/85 text-sm leading-relaxed mt-2">{PILLAR_DETAILS[results.primary_pillar].description}</p>
                {results.pillar_symptom_counts && (
                  <div className="space-y-1.5 mt-3 pt-3 border-t border-cream/20">
                    {Object.entries(results.pillar_symptom_counts).filter(([_, c]) => c > 0).sort((a, b) => b[1] - a[1]).map(([p, c]) => (
                      <div key={p} className="flex items-center gap-2">
                        <span className="text-cream/80 text-[10px] w-12">{PILLAR_DETAILS[p].label}</span>
                        <div className="flex-1 h-1.5 bg-cream/20 rounded-full"><div className="h-full bg-cream/70 rounded-full" style={{ width: `${Math.min(100, (c / results.total_symptoms) * 100)}%` }} /></div>
                        <span className="text-cream/60 text-[10px]">{c}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {results.top_categories?.length > 0 && (
                <div className="bg-white rounded-xl p-4 border border-burgundy/10">
                  <h3 className="font-serif font-bold text-burgundy text-sm mb-2">Your Priority Areas</h3>
                  <p className="text-xs text-burgundy/60 mb-2">Start with these in the Classroom:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {results.top_categories.map((cat) => <a key={cat} href={getCategoryClassroomUrl(cat)} className="px-3 py-1.5 bg-burgundy text-cream rounded-full text-xs font-semibold hover:bg-burgundy/80 transition-colors inline-flex items-center gap-1" data-testid={`result-tag-${cat.toLowerCase().replace(/[\s/]+/g, '-')}`}>{cat} <span className="text-cream/70">&rarr;</span></a>)}
                  </div>
                </div>
              )}
              <div className="bg-white rounded-xl p-4 border border-burgundy/10">
                <h3 className="font-serif font-bold text-burgundy text-sm mb-3">Recommended Next Steps</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {PILLAR_DETAILS[results.primary_pillar].recommendations.map((rec, i) => (
                    <div key={`rec-${rec.slice(0,20)}`} className="flex items-start gap-2 p-2.5 bg-cream/50 rounded-lg">
                      <div className="w-5 h-5 rounded-full bg-dark-teal flex items-center justify-center text-cream text-[10px] font-bold shrink-0 mt-0.5">{i + 1}</div>
                      <span className="text-xs text-burgundy/80">{rec}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Button onClick={handleRetake} variant="outline" className="border-burgundy/20 text-burgundy hover:bg-burgundy/5 flex-1">Retake Assessment</Button>
                <Button onClick={handleCopyLink} variant="outline" className="border-burgundy/20 text-burgundy hover:bg-burgundy/5 flex-1" data-testid="modal-share-btn">
                  {linkCopied ? <Check className="mr-1.5 h-4 w-4 text-dark-teal" /> : <Share2 className="mr-1.5 h-4 w-4" />}
                  {linkCopied ? 'Link Copied!' : 'Share Assessment'}
                </Button>
                <Button onClick={handleClose} className="bg-gradient-to-r from-burgundy to-dark-teal text-cream hover:opacity-90 flex-1">Done</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default WellnessAssessment;
